import { Checkbox } from "@/components/ui/checkbox";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { VisibilityState } from "@tanstack/react-table";
import { CopyMinus, Search, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SwitchList from "./SwitchList";

const Filter = ({
  projects,
  setProjectsFiltered,
  view,
  setView,
  defaultOpened,
  setDefaultOpened,
  projectsByPp,
  noSwitch,
  currentCarrier,
  columnVisibility,
  setColumnVisibility,
  setLoadingProjectsFiltered,
}) => {
  const navigate = useNavigate();
  const dateMin = projects.reduce((minDate, project) => {
    const projectDate = new Date(project.launchDate);
    return projectDate < minDate ? projectDate : minDate;
  }, new Date());
  const dateMax = projects.reduce((maxDate, project) => {
    const projectDate = new Date(project.launchDate);
    return projectDate > maxDate ? projectDate : maxDate;
  }, new Date());
  const maxExpectedDuration = projects.reduce((maxExpected, project) => {
    const projectExpectedDuration = project.expectedDuration;
    if (projectExpectedDuration != null) {
      // Vérifie si la durée est définie
      return projectExpectedDuration > maxExpected
        ? projectExpectedDuration
        : maxExpected;
    }
    return maxExpected; // Si la durée est nulle ou undefined, on conserve la valeur actuelle
  }, 0);

  const optionsStatus = [
    { value: "En cours", label: "En cours" },
    { value: "En attente de validation", label: "En attente de validation" },
    { value: "Validé", label: "Validé" },
    { value: "Rejeté", label: "Rejeté" },
  ];
  const optionsSold = [
    { value: "Non", label: "Non" },
    { value: "En cours", label: "En cours" },
    { value: "Oui", label: "Oui" },
  ];
  const optionsTypology = Array.from(
    new Set(projects.map((item) => item.type?.name))
  ).map((name) => ({
    value: name,
    label: name,
  }));

  const optionsCountry = Array.from(
    new Set(projects.map((item) => item.country?.name))
  ).map((name) => ({
    value: name as string,
    label: name as string,
  }));

  const optionsPorteur = Array.from(
    new Set(projects.map((item) => item.program?.carrier?.fullname))
  ).map((name) => ({
    value: name,
    label: name,
  }));

  const [filter, setFilter] = useState(() => ({
    search: "",
    status: optionsStatus.map((o) => o.value) as string[],
    typology: optionsTypology.map((o) => o.value) as string[],
    country: optionsCountry.map((o) => o.value) as string[],
    porteur: optionsPorteur.map((o) => o.value) as string[],
    sold: optionsSold.map((o) => o.value) as string[],
    rangeExpectedDuration: null,
    rangeLaunchDate: null,
  }));
  const handleChange = (slug, value) => {
    setFilter((prev) => {
      const updatedFilter = { ...prev };

      // Si c'est un champ multi-valeurs, on ajoute la valeur à un tableau existant
      if (["status", "typology", "country", "sold"].includes(slug)) {
        // Si la valeur est déjà un tableau, on ajoute la nouvelle valeur
        updatedFilter[slug] =
          Array.isArray(updatedFilter[slug]) &&
          updatedFilter[slug].includes(value)
            ? [...updatedFilter[slug], ...value]
            : value; // sinon, on crée un tableau avec la nouvelle valeur
      } else {
        // Sinon, c'est un champ mono-valeur
        updatedFilter[slug] = value;
      }

      // Convert filter object to a string-compatible format for URLSearchParams, excluding empty values
      const queryParams = new URLSearchParams(
        Object.entries(updatedFilter).reduce((acc, [key, val]) => {
          // On ne garde que les valeurs non vides, null ou undefined
          if (
            val !== null &&
            val !== undefined &&
            val !== "" &&
            val !== false
          ) {
            if (Array.isArray(val)) {
              // Si c'est un tableau, on ne l'ajoute que si le tableau contient des éléments
              if (val.length > 0) {
                acc[key] = val.join(","); // Joindre les éléments du tableau par des virgules
              }
            } else if (typeof val === "object") {
              acc[key] =
                typeof val === "object" ? JSON.stringify(val) : String(val);
            } else {
              acc[key] = String(val); // Sinon, convertir la valeur en chaîne
            }
          }
          return acc;
        }, {})
      );

      // Mise à jour de l'URL avec les nouveaux paramètres
      navigate({ search: queryParams.toString() });

      return updatedFilter;
    });
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const updatedFilter = { ...filter };
    let hasParams = false;

    queryParams.forEach((value, key) => {
      if (key !== "carrier" && key !== "columns") {
        hasParams = true;

        if (
          ["status", "typology", "country", "porteur", "sold"].includes(key)
        ) {
          updatedFilter[key] = value.split(",");
        } else if (
          key === "rangeExpectedDuration" ||
          key === "rangeLaunchDate"
        ) {
          try {
            updatedFilter[key] = JSON.parse(value);
          } catch {
            updatedFilter[key] = null;
          }
        } else if (key === "search") {
          updatedFilter[key] = value;
        }
      }
    });

    if (hasParams) {
      setFilter(updatedFilter);
    }
  }, []);

  useEffect(() => {
    setLoadingProjectsFiltered(true);
    const filteredProjects = projects.filter((project) => {
      const matchesSearch =
        !filter.search ||
        project.name.toLowerCase().includes(filter.search.toLowerCase());

      const statusMapping = {
        1: "En cours",
        2: "En attente de validation",
        3: "Validé",
        4: "Rejeté",
      };
      const soldMapping = {
        0: "Non",
        1: "En cours",
        2: "Oui",
      };

      // Gestion des statuts multiples
      const matchesStatus =
        !filter.status ||
        filter.status.length === 0 ||
        filter.status.includes(statusMapping[project.status]);
      // Gestion des typologies multiples
      const matchesTypology =
        !filter.typology.length || filter.typology.includes(project.type?.name);

      // Gestion des pays multiples
      const matchesCountry =
        !filter.country.length ||
        filter.country.includes(project.country?.name);

      // Gestion des porteurs multiples
      const matchesPorteur =
        !filter.porteur.length ||
        filter.porteur.includes(
          `${project.program?.carrier?.fullname || ""}`.trim()
        );

      // on accepte aussi les projets avec null si "1" est présent dans le filtre.
      const matchesSold =
        !filter.sold.length ||
        filter.sold.includes(soldMapping[project.soldStatus]) ||
        (project.soldStatus === null && filter.sold.includes(soldMapping[0]));

      const matchesRangeExpectedDuration =
        !filter.rangeExpectedDuration ||
        (project.expectedDuration >=
          parseInt(filter.rangeExpectedDuration.min) &&
          project.expectedDuration <=
            parseInt(filter.rangeExpectedDuration.max));

      const matchesRangeLaunchDate =
        !filter.rangeLaunchDate ||
        (new Date(project.launchDate) >=
          new Date(filter.rangeLaunchDate.dateMin) &&
          new Date(project.launchDate) <=
            new Date(filter.rangeLaunchDate.dateMax));

      return (
        matchesSearch &&
        matchesStatus &&
        matchesTypology &&
        matchesCountry &&
        matchesPorteur &&
        matchesSold &&
        matchesRangeExpectedDuration &&
        matchesRangeLaunchDate
      );
    });

    setProjectsFiltered(filteredProjects);
    setTimeout(() => {
      setLoadingProjectsFiltered(false);
    }, 0);
  }, [filter, projects]);
  const [searchValue, setSearchValue] = useState(filter.search);

  const [hasCarrier, setHasCarrier] = useState(
    new URLSearchParams(window.location.search).has("carrier")
  );
  const defaultVisibility: VisibilityState = {
    status: true,
    sold: true,
    typology: true,
    country: true,
    contributions: true,
    contributionCall: true,
    isFunded: true,
    projectOwner: false,
    launchDate: false,
    treesPlanted: false,
    regeneratedArea: false,
    CO2Sequestre: false,
    vegetationCover: false,
    jobCreation: false,
  };
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const columnsParam = searchParams.get("columns");

    if (columnsParam) {
      const visibleColumns = columnsParam.split(",");

      const updatedVisibility: VisibilityState = Object.fromEntries(
        Object.keys(defaultVisibility).map((key) => [
          key,
          visibleColumns.includes(key),
        ])
      ) as VisibilityState;

      setColumnVisibility(updatedVisibility);
    }
  }, []); // uniquement au montage

  useEffect(() => {
    const visibleKeys = Object.entries(columnVisibility)
      .filter(([_, value]) => value)
      .map(([key]) => key);

    // mettre à jour l'URL uniquement si elle est différente
    const currentParam = searchParams.get("columns") || "";
    const currentVisible = currentParam.split(",").filter(Boolean);
    const newVisible = visibleKeys.sort().join(",");

    if (currentVisible.sort().join(",") !== newVisible) {
      searchParams.set("columns", newVisible);
      setSearchParams(searchParams);
    }
  }, [columnVisibility, searchParams, setSearchParams]);
  useEffect(() => {
    const handleUrlChange = () => {
      const queryParams = new URLSearchParams(window.location.search);
      setHasCarrier(queryParams.has("carrier"));
    };

    window.addEventListener("popstate", handleUrlChange);
    window.addEventListener("pushstate", handleUrlChange);
    window.addEventListener("replacestate", handleUrlChange);

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
      window.removeEventListener("pushstate", handleUrlChange);
      window.removeEventListener("replacestate", handleUrlChange);
    };
  }, []);
  const allProgramIds = Object.values(projectsByPp).map(
    (program: { program: { id: number } }) => String(program.program.id)
  );
  const toggleAllAccordions = () => {
    if (defaultOpened.length > 0) {
      // Si au moins un accordéon est ouvert, fermer tous
      setDefaultOpened([]);
    } else {
      // Sinon, ouvrir tous les accordéons
      setDefaultOpened(allProgramIds);
    }
  };
  return (
    <div className={`${hasCarrier ? "-mt-14" : ""}`}>
      <div className="flex items-center justify-between search-buttons mb-[20px]">
        <div className="md:w-1/4">
          <div className="relative">
            <Input
              type="search"
              placeholder="Rechercher un projet"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onBlur={(e) => handleChange("search", e.target.value)}
              className="h-11 text-[14px]"
            />
            <Search
              size={16}
              className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-between flex-wrap items-start">
        <div className="md:w-2/3 flex gap-x-3 gap-y-2 items-center flex-wrap md:mb-0 mb-5">
          <DropdownFilter
            options={optionsStatus}
            name="Status"
            slug="status"
            value={filter.status}
            handleChange={handleChange}
            key="status"
          />
          <DropdownFilter
            options={optionsTypology}
            name="Typologie"
            slug="typology"
            value={filter.typology}
            handleChange={handleChange}
            key="typology"
          />
          <DropdownFilter
            options={optionsCountry}
            name="Pays"
            slug="country"
            value={filter.country}
            handleChange={handleChange}
            key="country"
          />
          <DropdownFilter
            options={optionsPorteur}
            name="Porteur"
            slug="porteur"
            value={filter.porteur}
            handleChange={handleChange}
            key="porteur"
          />
          <DropdownFilter
            options={optionsSold}
            name="Vendu ?"
            slug="sold"
            value={filter.sold}
            handleChange={handleChange}
            key="sold"
          />

          <SlideFilter
            name="Durée du projet : "
            slug="rangeExpectedDuration"
            maxMin={{ min: 1, max: maxExpectedDuration }}
            value={filter.rangeExpectedDuration}
            handleChange={handleChange}
          />
          <div className="w-full">
            {filter.rangeLaunchDate ? (
              <DateRangePicker
                onUpdate={(values) =>
                  handleChange("rangeLaunchDate", {
                    dateMin: values.range.from,
                    dateMax: values.range.to,
                  })
                }
                initialDateFrom={filter.rangeLaunchDate.dateMin || dateMin}
                initialDateTo={filter.rangeLaunchDate.dateMax || dateMax}
                align="start"
                locale="fr-FR"
                showCompare={false}
              />
            ) : (
              <button
                onClick={() =>
                  handleChange("rangeLaunchDate", {
                    dateMin: dateMin,
                    dateMax: dateMax,
                  })
                }
                className="text-xs py-[3px] h-[26px] border rounded-md px-3 bg-background hover:bg-foreground hover:text-background"
              >
                Filtrer par dates de lancement
              </button>
            )}
          </div>
        </div>
        <div className="w-full md:w-1/3 relative z-50">
          <div className="flex justify-end gap-2">
            {view === "list" && (
              <div
                className="flex items-center cursor-pointer gap-2 mr-4"
                onClick={toggleAllAccordions}
              >
                <CopyMinus size={15} />{" "}
                {defaultOpened.length > 0 ? "Plier tout" : "Déplier tout"}
              </div>
            )}
            {!noSwitch && (
              <div>
                <SwitchList view={view} setView={setView} />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-4">
            {!Object.entries(filter).every(([key, value]) => {
              const defaultValue = {
                search: "",
                status: optionsStatus.map((o) => o.value),
                typology: optionsTypology.map((o) => o.value),
                country: optionsCountry.map((o) => o.value),
                porteur: optionsPorteur.map((o) => o.value),
                sold: optionsSold.map((o) => o.value),
                rangeExpectedDuration: null,
                rangeLaunchDate: null,
              }[key];

              if (Array.isArray(value)) {
                return (
                  Array.isArray(defaultValue) &&
                  value.length === defaultValue.length &&
                  value.every((v) => defaultValue.includes(v))
                );
              }

              return value === defaultValue;
            }) && (
              <div className="flex justify-end">
                <button
                  className="outline-none border-0 border-b flex gap-1 items-center"
                  onClick={() => {
                    // Réinitialisation des filtres
                    setFilter({
                      search: "",
                      status: optionsStatus.map((o) => o.value) as string[],
                      typology: optionsTypology.map((o) => o.value) as string[],
                      country: optionsCountry.map((o) => o.value) as string[],
                      porteur: optionsPorteur.map((o) => o.value) as string[],
                      sold: optionsSold.map((o) => o.value) as string[],
                      rangeExpectedDuration: null,
                      rangeLaunchDate: null,
                    });

                    // Suppression des paramètres de l'URL
                    const queryParams = new URLSearchParams(
                      window.location.search
                    );
                    queryParams.forEach((_, key) => {
                      if (key !== "carrier" && key !== "columns") {
                        queryParams.delete(key);
                      }
                    });

                    navigate({ search: queryParams.toString() });
                  }}
                >
                  <X size={14} className="text-red-600" /> Réinitialiser les
                  filtres
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SlideFilter = ({
  name,
  slug,
  value,
  maxMin,
  handleChange,
}: {
  name: string;
  slug: string;
  value: { min: number; max: number };
  maxMin: { min: number; max: number };
  handleChange: (slug: string, value: { min: number; max: number }) => void;
}) => {
  const [range, setRange] = useState([
    value?.min || maxMin.min,
    value?.max || maxMin.max,
  ]);
  const handleRangeChange = (value) => {
    setRange(value);
  };
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) {
      if (range[0] != maxMin.min || range[1] != maxMin.max) {
        handleChange(slug, {
          min: range[0],
          max: range[1],
        });
      } else {
        // console.log("quand c’est pas le cas");
      }
    }
  }, [open]);
  return (
    <div className="text-xs py-[3px] h-[26px] border rounded-md px-2 bg-background hover:bg-foreground hover:text-background">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="text-xs py-[3px] h-[26px]">
            {name}
            {`${value?.min || range[0]} à ${value?.max || range[1]} mois`}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <Slider
            max={maxMin.max}
            min={maxMin.min}
            step={1}
            minStepsBetweenThumbs={1}
            value={[value?.min || range[0], value?.max || range[1]]}
            onValueChange={handleRangeChange}
            formatLabel={(value) => `${value} mois`}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
const DropdownFilter = ({
  options,
  name,
  value,
  slug,
  handleChange,
}: {
  options: any[];
  name: string;
  value: string[];
  slug: string;
  handleChange: (slug: string, value: string[]) => void;
}) => {
  const [selectedValues, setSelectedValues] = React.useState<string[]>(value);

  useEffect(() => {
    setSelectedValues(value);
  }, [value]);

  const handleBlur = () => {
    const hasChanged =
      selectedValues.length !== value.length ||
      !selectedValues.every((val) => value.includes(val));

    if (hasChanged) {
      handleChange(slug, selectedValues);
    }
  };

  const handleCheckboxChange = (optionValue: string, isChecked: boolean) => {
    setSelectedValues((prev) => {
      if (isChecked) {
        if (!prev.includes(optionValue)) {
          return [...prev, optionValue];
        }
      } else {
        return prev.filter((item) => item !== optionValue);
      }
      return prev;
    });
  };

  const allSelected = options.every((opt) =>
    selectedValues.includes(opt.value)
  );

  const toggleAll = (checked: boolean) => {
    if (checked) {
      const all = options.map((opt) => opt.value);
      setSelectedValues(all);
      handleChange(slug, all);
    } else {
      setSelectedValues([]);
      handleChange(slug, []);
    }
  };

  return (
    <div className="relative">
      <DropdownMenu onOpenChange={(open) => !open && handleBlur()}>
        <DropdownMenuTrigger asChild>
          <button className="text-xs py-[3px] h-[26px] border  rounded-md px-3 font-normal">
            {name}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="max-h-64 overflow-auto"
        >
          <DropdownMenuLabel className="flex justify-between">
            <div className="flex justify-between w-full">
              <div className="flex gap-1 items-center">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                {name}
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.map(
            (option, i) =>
              option.label && (
                <DropdownMenuCheckboxItem
                  key={i}
                  checked={selectedValues.includes(option.value)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange(option.value, checked)
                  }
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              )
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default Filter;
